import { Alert, Anchor } from '@zillow/constellation-10';

import { EXTERNAL_URLS } from '@zg-rentals/zillow-rental-manager/constants';

import {
  hasVerificationFailed,
  isAwaitingManualReview,
  isOwnershipVerified,
  isVerificationNotApplicable,
} from '../../hooks/queries/useOwnershipVerification';

import type { ReactNode } from 'react';
import type { PropertyOwnershipVerificationState } from '@zg-rentals/ts-rental-manager-api-client';

export const OWNERSHIP_VERIFICATION_ERROR_COPY = {
  /** Also covers a 200 that carried no inquiry. */
  cannotStart: 'Verification couldn’t be started',
  cannotLoad: 'Verification couldn’t be loaded',
  cannotCheckStatus: 'Verification status couldn’t be checked',
  /** Same failure, said where publishing is the thing it blocks. */
  cannotCheckStatusBeforePublish: 'Verification status couldn’t be checked, so this listing can’t be published yet.',
} as const;

export type OwnershipVerificationStatusProps = {
  state: PropertyOwnershipVerificationState | undefined;
  /** Rendered for a state with no settled outcome, which differs by surface. */
  fallback?: ReactNode;
};

/**
 * The settled outcomes of ownership verification, shared by the create-listing step and the mobile
 * handoff page. Branches off the same predicates the wizard gates on, so the copy and the gating can
 * never disagree about what a state means.
 */
const OwnershipVerificationStatus = ({ state, fallback = null }: OwnershipVerificationStatusProps) => {
  if (isOwnershipVerified(state)) {
    return (
      <Alert tone="success" data-testid="OwnershipVerificationVerified">
        Your identity and ownership are verified
      </Alert>
    );
  }

  if (isAwaitingManualReview(state)) {
    return (
      <Alert tone="info" data-testid="OwnershipVerificationUnderReview">
        Verification is under review. We’ll email you when it’s done. This listing can’t be published until then.
      </Alert>
    );
  }

  if (isVerificationNotApplicable(state)) {
    return (
      <Alert tone="info" data-testid="OwnershipVerificationNotApplicable">
        This listing doesn’t require identity and ownership verification.
      </Alert>
    );
  }

  if (hasVerificationFailed(state)) {
    return (
      <Alert tone="critical" data-testid="OwnershipVerificationFailed">
        Ownership couldn’t be verified, so this listing can’t be published.{' '}
        <Anchor href={EXTERNAL_URLS.SUPPORT_EMAIL}>Contact support</Anchor> if you think this is a mistake.
      </Alert>
    );
  }

  return fallback;
};

export default OwnershipVerificationStatus;
